import React, { useState, useRef } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import {
  useTranslator,
  FormikInput as Input,
  FormikSelect as Select,
  useToast,
  callNativeAppRemoveLastRoute,
  useAppContext,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { useNavigate } from 'react-router-dom';
import { useGlobalIngredientsEdit } from '../hooks/useGlobalIngredientsEdit';
import { Ingredient } from '../types';
import validationSchema from './validationSchema';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';

interface AnalyzeIngredientInput {
  description: string;
  baseAmount: number;
  baseUnit: string;
}

interface AnalyzeIngredientResponse {
  __typename: string;
  primaryIngredient: {
    productId: string;
    name: string;
    brand?: string;
    baseAmount: number;
    baseUnit: string;
    calories: number;
    proteinGrams: number;
    carbsGrams: number;
    fatGrams: number;
  };
}

type TrackerAddIngredientFormValues = {
  description: string;
  baseAmount: number | null;
  baseUnit: string;
};

interface TrackerAddIngredientContainerProps {
  analyzeIngredient: (input: AnalyzeIngredientInput) => Promise<AnalyzeIngredientResponse>;
  cancelAnalyzeIngredient: () => void;
}

const TrackerAddIngredientContainer: React.FC<TrackerAddIngredientContainerProps> = ({
  analyzeIngredient,
  cancelAnalyzeIngredient,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const { toastError, toastSuccess } = useToast();
  const { ingredients, updateIngredients } = useGlobalIngredientsEdit([]);
  const isCanceledRef = useRef(false);

  const [showNoFoodAlert, setShowNoFoodAlert] = useState(false);

  const {
    images: { aiStarDarkIcon },
  } = useAppContext();

  // Get multiplier from sessionStorage (default to 1 if not found)
  const multiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');

  // Get system of measures from localStorage
  const systemOfMeasures = localStorage.getItem('systemOfMeasures') || 'si';

  const handleSubmit = async (
    values: TrackerAddIngredientFormValues,
    { resetForm, setSubmitting }: FormikHelpers<TrackerAddIngredientFormValues>
  ) => {
    // Prevent retry if user canceled the previous request
    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setSubmitting(false);
      return;
    }

    if (values.baseAmount == null) {
      setSubmitting(false);
      toastError();
      return;
    }

    const request = {
      description: values.description,
      baseAmount: values.baseAmount,
      baseUnit: values.baseUnit,
    };

    try {
      const response = await analyzeIngredient(request);

      if (response.__typename === 'AnalyzedDailyTrackerIngredients') {
        setShowNoFoodAlert(false);
        const primaryIngredient = response.primaryIngredient;

        // User entered amounts for current number of portions
        // We need to divide by multiplier to store for 1 portion in sessionStorage
        const amountFor1Portion = primaryIngredient.baseAmount / multiplier;
        const caloriesFor1Portion = Math.round(primaryIngredient.calories / multiplier);
        const proteinFor1Portion =
          Math.round((primaryIngredient.proteinGrams / multiplier) * 100) / 100;
        const fatFor1Portion = Math.round((primaryIngredient.fatGrams / multiplier) * 100) / 100;
        const carbsFor1Portion =
          Math.round((primaryIngredient.carbsGrams / multiplier) * 100) / 100;

        // Transform API response to Ingredient type (stored for 1 portion)
        const newIngredient: Ingredient = {
          id: primaryIngredient.productId,
          name: primaryIngredient.name,
          brand: primaryIngredient.brand,
          kcal: caloriesFor1Portion,
          measurements: [
            {
              amount: amountFor1Portion,
              text: `${amountFor1Portion} ${primaryIngredient.baseUnit}`,
              unit: primaryIngredient.baseUnit,
            },
          ],
          macro: {
            protein: { grams: proteinFor1Portion },
            fat: { grams: fatFor1Portion },
            carbohydrates: { grams: carbsFor1Portion },
          },
        };

        // Add new ingredient to the list (will be stored in sessionStorage via useGlobalIngredientsEdit)
        updateIngredients([...ingredients, newIngredient]);

        navigate(-1);
        callNativeAppRemoveLastRoute();
        toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
      } else if (response.__typename === 'NotFoodError') {
        setShowNoFoodAlert(true);
      } else {
        toastError();
      }
    } catch (error) {
      // Don't show error if operation was canceled
      // Note: setSubmitting(false) is already called in handleCancel
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }
      // For other errors, reset submitting state and show error
      setSubmitting(false);
      toastError();
    }
  };

  const shortenUnit = (unit: string) => {
    if (unit === 'grams') {
      return 'g';
    } else if (unit === 'milliliters') {
      return 'ml';
    } else if (unit === 'oz') {
      return 'oz';
    } else if (unit === 'fl_oz') {
      return 'fl oz';
    } else {
      return unit;
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.addIngredient.navbarTitle')}</TitleBar>

      <Formik<TrackerAddIngredientFormValues>
        onSubmit={handleSubmit}
        initialValues={{
          description: '',
          baseAmount: null,
          baseUnit: systemOfMeasures === 'si' || systemOfMeasures === 'si_cl' ? 'grams' : 'oz',
        }}
        validationSchema={validationSchema}
      >
        {({ isValid, values, isSubmitting, setSubmitting }) => {
          const handleCancel = () => {
            // Set flag to prevent Formik from retrying the submit
            isCanceledRef.current = true;
            // Cancel the ongoing GraphQL mutation
            cancelAnalyzeIngredient();
            // Reset submitting state immediately
            setSubmitting(false);
            // Clear form fields individually to avoid validation
            setTimeout(() => {
              isCanceledRef.current = false;
            }, 0);
          };

          return (
            <Form>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col xl={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <Input
                            name="description"
                            label={t('tracker.logMeal.addIngredient.nameInput')}
                            type="text"
                            translateErrMsg={true}
                          />
                          <Row>
                            <Col xs="6" sm="6" className="pe-1">
                              <Input
                                name="baseAmount"
                                label={t('tracker.logMeal.addIngredient.amountInput')}
                                type="number"
                                unit={shortenUnit(values.baseUnit)}
                                translateErrMsg={true}
                                step={0.1}
                                placeholder="0"
                              />
                            </Col>

                            <Col xs="6" sm="6" className="ps-1">
                              <Select name="baseUnit" label={' '} inline={false}>
                                {systemOfMeasures === 'si' && (
                                  <>
                                    <option value="grams">{t('input.select.unit.grams')}</option>
                                    <option value="milliliters">
                                      {t('input.select.unit.milliliters')}
                                    </option>
                                  </>
                                )}
                                {systemOfMeasures === 'si_cl' && (
                                  <>
                                    <option value="grams">{t('input.select.unit.grams')}</option>
                                    <option value="cl">{t('input.select.unit.centiliters')}</option>
                                  </>
                                )}
                                {systemOfMeasures === 'imperial' && (
                                  <>
                                    <option value="oz">{t('input.select.unit.ounces')}</option>
                                    <option value="fl_oz">
                                      {t('input.select.unit.fluidOunces')}
                                    </option>
                                  </>
                                )}
                              </Select>
                            </Col>
                          </Row>

                          {showNoFoodAlert && (
                            <Alert color="warning" className="mt-xl mb-0" fade={false}>
                              <p className="mb-0">
                                {t('tracker.describeFood.analyze.noFood.notification')}
                              </p>
                            </Alert>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    {!isSubmitting ? (
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={!isValid}
                            className="w-100"
                          >
                            {t('tracker.logMeal.addIngredient.addButton')}
                          </Button>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col xs="12" className="text-center">
                          <p className="description text-text-variant-color2">
                            {t('tracker.describeFood.loader.analyzing')}
                          </p>
                        </Col>
                        <Col xs="4">
                          <Button type="button" color="link" onClick={handleCancel}>
                            {t('tracker.describeFood.loader.cancelButton')}
                          </Button>
                        </Col>
                        <Col xs="4">
                          <div className="ai-star">
                            <img src={aiStarDarkIcon} alt="" />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Container>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default TrackerAddIngredientContainer;
